/**
 * Types for rebate reports
 * Used by both backend (report generation) and frontend (display)
 */

export enum RebateReportType {
  DIVISION_REBATE_SUMMARY = "DIVISION_REBATE_SUMMARY",
  SUPPLIER_TAG_SUMMARY = "SUPPLIER_TAG_SUMMARY",
  DAILY_EARNINGS_PYE = "DAILY_EARNINGS_PYE",
}

export type ReportLineType = "estimates" | "predictions" | "reported";

/**
 * ReportLineType constants for better type safety
 * Use ReportLineTypeValues.ESTIMATES instead of "estimates"
 */
export const ReportLineTypeValues = {
  ESTIMATES: "estimates" as const,
  PREDICTIONS: "predictions" as const,
  REPORTED: "reported" as const,
} as const;

// Extended type for cell-level styling (includes additional display-only types)
export type CellLineType = ReportLineType | "payment" | "adjustment";

export type ReportTimeFilter = "ytd" | "all" | "current";

export interface DivisionRebateSummaryRequest {
  year: number;
  divisionIds?: string[];
  lineType: ReportLineType; // 'reported' by default
  categoryFilters?: string[]; // Filter by categories: 'Redistribution', 'Conditional', 'Non-Conditional'
}

export interface ReportTableCell {
  value: number;
  formattedValue: string;
  lineType?: CellLineType; // Optional: indicates data type for color coding (reported=black, predictions=blue, estimates=green, payment=purple)
  isFutureMonth?: boolean; // Optional: true if this is a future month (should show '-' instead of $0.00)
}

export interface ReportTableRow {
  label: string;
  id: string;
  cells: ReportTableCell[];
  total?: ReportTableCell;
}

export interface ReportTable {
  title: string;
  columnHeaders: string[];
  rows: ReportTableRow[];
  grandTotal?: ReportTableCell;
}

export interface DivisionRebateSummaryResponse {
  year: number;
  divisionNames: string[];
  lineType: ReportLineType;
  generatedAt: Date;
  ruleTagTable: ReportTable;
  supplierTable: ReportTable;
}

export interface SupplierTagSummaryRequest {
  year: number;
  divisionIds?: string[];
  supplierTagIds?: string[];
  lineType: ReportLineType; // 'reported' by default
}

export interface SupplierTagSummaryResponse {
  year: number;
  divisionNames: string[];
  supplierTagNames: string[];
  lineType: ReportLineType;
  generatedAt: Date;
  supplierTagTable: ReportTable;
}

export interface DailyEarningsPYERequest {
  year: number; // The year to display (e.g., 2024)
  divisionIds?: string[];
  categoryFilters?: string[]; // Filter by categories: 'Redistribution', 'Conditional', 'Non-Conditional'
  priorYearLineType?: ReportLineType; // 'reported', 'estimates', or 'predictions' - defaults to 'estimates'
  throughMonth?: number; // 1-12; when set, caps reported data to Jan–throughMonth instead of showing full year
}

export interface DailyEarningsPYEResponse {
  year: number; // Current year being displayed (e.g., 2024)
  priorYear: number; // Comparison year (e.g., 2023)
  divisionNames: string[];
  generatedAt: Date;
  table: ReportTable; // 6 columns: Division Code, Conditional YTD, Non-Conditional YTD, Re-Distribution YTD, Year Total YTD, PriorYear Total YTD
  monthlyBreakdownTable: ReportTable; // Monthly breakdown: Division Code | Total YTD (estimates) | Total Reported YTD | Post Y/E Adj #1 | Dec | Nov | ... | Jan
  /** ISO date of the most recent PYE snapshot used for the monthly breakdown table, or null if no snapshots exist */
  lastSnapshotDate: string | null;
}

export interface SupplierPaymentTermsRequest {
  year: number;
}

export interface SupplierPaymentTermsItem {
  supplier: string;
  supplierCode: string;
  agreement: string;
  rebatePaymentTerms: string;
  purchasePaymentTerms: string;
}

export interface SupplierPaymentTermsResponse {
  year: number;
  generatedAt: Date;
  data: SupplierPaymentTermsItem[];
}

export interface MonthlyTrendingRequest {
  year: number;
  divisionIds?: string[];
  priorYearLineType?: ReportLineType; // 'reported', 'estimates', or 'predictions' - defaults to 'estimates'
  throughMonth?: number; // 1-12; when set, caps data to Jan–throughMonth instead of using the system month
}

export interface MonthlyTrendingBreakdownTables {
  total: ReportTable;
  redistribution: ReportTable;
  conditional: ReportTable;
  nonConditional: ReportTable;
}

export interface MonthlyTrendingResponse {
  year: number;
  priorYear: number;
  divisionNames: string[];
  generatedAt: Date;
  table: ReportTable; // Comparative: Conditional, Non-Conditional, Redistribution, Total YTD, Prior Year Total YTD
  monthlyBreakdownTables: MonthlyTrendingBreakdownTables; // One monthly breakdown table per rule category plus a combined total
}

// ============================================================================
// Budget vs. Forecast Comparison Report
// ============================================================================

/** Categories for external figures (budget data uploaded from Income Statement) */
export type RebateExternalFigureCategory =
  | "Redistribution"
  | "Non-Conditional"
  | "Conditional";

/** Types of external figures (generalizable for future use) */
export type RebateExternalFigureType = "budget";

export interface BudgetComparisonRequest {
  year: number;
  categoryFilters?: string[]; // Filter by categories: 'Redistribution', 'Conditional', 'Non-Conditional'
  lineType?: ReportLineType; // 'predictions' | 'reported' | 'estimates' — defaults to 'predictions'
}

export interface BudgetComparisonCategoryMonth {
  month: number; // 1-12
  forecast: number | null; // value of the selected line type for the month; null when forecast is not applicable (e.g., future month for reported/estimates)
  budget: number | null;
  delta: number | null; // forecast - budget
}

export interface BudgetComparisonCategory {
  name: RebateExternalFigureCategory;
  months: BudgetComparisonCategoryMonth[];
  totalForecast: number;
  totalBudget: number | null;
  totalDelta: number | null;
}

export interface BudgetComparisonResponse {
  year: number;
  lineType: ReportLineType;
  generatedAt: Date;
  hasBudgetData: boolean;
  categories: BudgetComparisonCategory[];
  totals: {
    months: BudgetComparisonCategoryMonth[];
    totalForecast: number;
    totalBudget: number | null;
    totalDelta: number | null;
  };
}

export interface BudgetUploadValidateResponse {
  valid: boolean;
  year: number | null;
  categoriesFound: string[];
  monthsFound: number[];
  totalRecords: number;
  warnings: string[];
}

export interface BudgetUploadResponse {
  success: boolean;
  year: number;
  recordsInserted: number;
  recordsDeleted: number;
}

export interface SupplierYoYRequest {
  year: number;
  divisionIds?: string[];
  thisYearLineType: ReportLineType;
  lastYearLineType: ReportLineType;
  categoryFilters?: string[]; // Filter by categories: 'Redistribution', 'Conditional', 'Non-Conditional'
  throughMonth?: number; // 1-12; when set, caps data to Jan–throughMonth instead of using the system month
}

export interface SupplierYoYResponse {
  year: number;
  priorYear: number;
  divisionNames: string[];
  thisYearLineType: ReportLineType;
  lastYearLineType: ReportLineType;
  generatedAt: Date;
  table: ReportTable;
}

export type ProfitabilityDimension = "division" | "supplier" | "productFamily";

export interface ProfitabilityReportRequest {
  year: number;
  lineType?: ReportLineType; // 'reported' | 'estimates' | 'predictions', defaults to 'reported'
  /** @deprecated Use `dimensions` instead */
  viewMode?:
    | "supplier"
    | "division"
    | "productFamilyDesc"
    | "divisionHierarchy";
  dimensions?: [
    ProfitabilityDimension,
    ProfitabilityDimension,
    ProfitabilityDimension,
  ];
  divisionIds?: string[];
  supplierIds?: string[];
  productFamilyDescs?: string[]; // Filter by specific product family descriptions
  month?: number | null; // 1-12 for Jan-Dec, null/undefined for YTD (default)
  shipTypes?: number[]; // Filter by ship types [1, 2, 3] - default is all three
}

export interface ProfitabilityReportRow {
  supplierId?: string;
  supplierName?: string;
  divisionId?: string;
  divisionName?: string;
  productFamilyDesc?: string;
  internalCode?: string; // Supplier code for primary rows, division code for secondary rows
  dimensionLabel?: string; // Display label for the row (used by generic hierarchy)
  dimensionType?: ProfitabilityDimension; // Which dimension this row represents
  cogs: number;
  landedMarginPercent: number;
  nonConditional: number;
  nonConditionalPercent: number;
  conditional: number;
  conditionalPercent: number;
  redistribution: number;
  redistributionPercent: number;
  totalRebates: number;
  totalRebatesPercent: number;
  totalProfitPercent: number;
  children?: ProfitabilityReportRow[]; // Hierarchical sub-rows
}

/**
 * Represents a rebate breakdown that could not be matched to sales margin data
 */
export interface UnmatchedBreakdown {
  supplierId: string;
  supplierName: string;
  divisionId: string;
  divisionName: string;
  agreementId: string;
  rebateAmount: number;
  reason:
    | "no_sales_margin"
    | "zero_cogs"
    | "no_matching_families"
    | "no_hierarchy_node";
}

export interface ProfitabilityReportResponse {
  year: number;
  generatedAt: Date;
  rows: ProfitabilityReportRow[];
  dateRange?: {
    minYearMonth: string; // YYYY-MM format
    maxYearMonth: string; // YYYY-MM format
  };
  unmatchedBreakdowns?: UnmatchedBreakdown[];
}

export interface SupplierSalesMarginUploadResponse {
  success: boolean;
  rowsProcessed: number;
  rowsInserted: number;
  rowsUpdated: number;
  rowsSkipped?: number; // Number of rows skipped due to unmatched suppliers
  rowsDeleted?: number; // Number of existing records deleted for the year before insert
  errors?: Array<{
    row: number;
    message: string;
  }>;
}

/**
 * Response from the validation endpoint for profitability data upload.
 * Returns unmatched supplier codes so the user can decide whether to proceed.
 */
export interface SupplierSalesMarginValidateResponse {
  valid: boolean;
  totalRows: number;
  matchedRows: number;
  unmatchedSupplierCodes: Array<{
    code: string;
    rowCount: number; // Number of rows with this supplier code
  }>;
  unmatchedDivisions: Array<{
    name: string;
    rowCount: number;
  }>;
}

/**
 * PYE (Post Year-End) Snapshot Types
 *
 * Stored as JSONB in the report_snapshots table.
 * Each snapshot captures division-level rebate totals at a point in time.
 */

export interface PYESnapshotDivisionData {
  divisionName: string;
  divisionCode: string | null;
  /** Total YTD rebate amount at snapshot time */
  totalYTD: number;
  conditional: number;
  nonConditional: number;
  redistribution: number;
}

export interface PYESnapshotData {
  /** The fiscal year being tracked (e.g., 2025) */
  fiscalYear: number;
  /** Whether this is the baseline (true) or a monthly snapshot (false) */
  isBaseline: boolean;
  /** Per-division totals keyed by divisionId */
  divisions: Record<string, PYESnapshotDivisionData>;
}
